import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState(localStorage.getItem("activeCompanyId"));
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Set active company header for all requests
  useEffect(() => {
    if (activeCompanyId) {
      axios.defaults.headers.common["X-Company-ID"] = activeCompanyId;
      localStorage.setItem("activeCompanyId", activeCompanyId);
    } else {
      delete axios.defaults.headers.common["X-Company-ID"];
      localStorage.removeItem("activeCompanyId");
    }
  }, [activeCompanyId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      // For Super Admin, fetch all companies
      if (response.data.role === "super_admin") {
        try {
          const companiesRes = await axios.get(`${API_URL}/companies`);
          setCompanies(companiesRes.data);
          // Set default active company if not set
          if (!activeCompanyId && response.data.company_id) {
            setActiveCompanyId(response.data.company_id);
          }
        } catch (e) {
          console.error("Failed to fetch companies:", e);
        }
      } else {
        // Non-super admins use their assigned company
        setActiveCompanyId(response.data.company_id);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (userCode, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { user_code: userCode, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    // Set active company from user's company
    if (userData.company_id) {
      setActiveCompanyId(userData.company_id);
    }
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("activeCompanyId");
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Company-ID"];
    setToken(null);
    setUser(null);
    setActiveCompanyId(null);
    setCompanies([]);
  };

  const switchCompany = (companyId) => {
    setActiveCompanyId(companyId);
    // Reload the page to refresh data with new company context
    window.location.reload();
  };

  const getActiveCompany = () => {
    return companies.find(c => c.id === activeCompanyId) || null;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      register, 
      logout, 
      loading,
      activeCompanyId,
      companies,
      switchCompany,
      getActiveCompany
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
